import { RequestData } from '@ant-design/pro-table';
import { request } from '@/utils/request';
import { vanillaClient } from '@/trpc/client';
import { normalizeEmail, normalizeEmailFields } from '@/utils/email';

export interface OperatorListItem {
  id: string;
  name: string;
  active: boolean;
  email: string;
  mfa: string;
  hardwareWalletLabel?: string;
  locked: boolean;
  vendorIds?: number[];
  vendorsName: string[];
  /** Department tags for team board visibility */
  departmentTags?: string[];
  mfaEnrollmentStatus?: 'pending' | 'completed';
  /** When true, operator may only access users whose groups intersect allowedUserGroupIds */
  userGroupRestricted?: boolean;
  allowedUserGroupIds?: string[] | null;
}

export interface OperatorQueryParams {
  current?: number;
  pageSize?: number;
  id?: string;
  email?: string;
  vendorId?: number;
  status?: string;
}

export async function query(params: OperatorQueryParams): Promise<RequestData<OperatorListItem>> {
  return request<RequestData<OperatorListItem>>('/operators', {
    params: normalizeEmailFields(params, ['email'])
  });
}
export async function search(params: any): Promise<RequestData<OperatorListItem>> {
  return request<RequestData<OperatorListItem>>('/operators/search', {
    params: normalizeEmailFields(params, ['email'])
  });
}

export async function unlockOperator(params: { operatorId: string; operatorRemarks: string }): Promise<boolean> {
  return await request<boolean>('/operators/unlock', {
    method: 'POST',
    data: params
  });
}

export async function updateOperatorEmail(params: {
  id: string;
  email: string;
  _yotpToken: string;
}): Promise<{ success: boolean; email: string }> {
  return request(`/operators/${params.id}/email`, {
    method: 'POST',
    data: { email: normalizeEmail(params.email), _yotpToken: params._yotpToken }
  });
}

export async function updateAllowedUserGroups(params: {
  operatorId: string;
  allowedUserGroupIds: string[] | null;
  _yotpToken: string;
}): Promise<{ success: boolean; allowedUserGroupIds: string[] | null }> {
  const r = await vanillaClient.operators.updateAllowedUserGroups.mutate({
    operatorId: params.operatorId,
    allowedUserGroupIds: params.allowedUserGroupIds,
    _yotpToken: params._yotpToken
  });
  return { success: r.success, allowedUserGroupIds: r.allowedUserGroupIds };
}

export interface BulkPermissionResult {
  succeeded: string[];
  failed: { operatorId: string; error: string }[];
}

export async function bulkCopyPermissions(params: {
  sourceOperatorId: string;
  targetOperatorIds: string[];
  _yotpToken: string;
}): Promise<BulkPermissionResult> {
  return request<BulkPermissionResult>('/operators/bulk-copy-permissions', {
    method: 'POST',
    data: params
  });
}

export async function bulkModifyPermissions(params: {
  operatorIds: string[];
  addPermissions?: Record<string, string[]>;
  removePermissions?: Record<string, string[]>;
  _yotpToken: string;
}): Promise<BulkPermissionResult> {
  return request<BulkPermissionResult>('/operators/bulk-modify-permissions', {
    method: 'POST',
    data: params
  });
}
